import { useState, useContext, useCallback, useEffect } from "react";
import useApi from "../../../../../utils/api-manager/Helper/useApi";
import AuthContext from "../../../../../utils/secure-route/AuthContext";
import { useStore } from "../../../../../context/StoreContext";
import { toast } from "react-toastify";

// Requester (destination) side transitions and reviewer (source) side
// transitions the SERVER enforces (see StockTransferRequest.model on the
// backend) - mirrored here only to decide which action buttons to show,
// never trusted as the actual authorization.
export const SOURCE_TRANSITIONS = {
  requested: ["approved", "rejected"],
  approved: ["packing"],
  packing: ["sent_to_courier"],
  sent_to_courier: ["sent"],
};
export const DESTINATION_TRANSITIONS = {
  requested: ["cancelled"],
  sent: ["received"],
};

export const STATUS_LABELS = {
  requested: "Requested",
  approved: "Approved",
  rejected: "Rejected",
  packing: "Packing",
  sent_to_courier: "Sent to Courier",
  sent: "Sent",
  received: "Received",
  cancelled: "Cancelled",
};

const StockTransferController = () => {
  const { Get, GetPaginatedData, Post, Patch, getAPI } = useApi();
  const { user } = useContext(AuthContext);
  const myStoreId = user?.store;
  const { stores } = useStore();

  // ---- "Find Product in Other Store" ----
  // Searches EVERY store in the same BUSINESS as this one at once (rather
  // than making the user pick one store first) and tags each result with
  // which store it came from, so a product search just surfaces wherever
  // it's actually in stock.
  //
  // This deliberately does NOT reuse `stores` from useStore() - that list
  // is scoped to the logged-in user's OWN store memberships (used for the
  // store switcher), so an employee added only to Store 3 would never see
  // Store 2's stock even though both stores belong to the same owner. The
  // `storeNetwork` endpoint instead returns every store sharing an active
  // owner with the caller's current store, regardless of the caller's own
  // personal membership grants.
  const [otherStores, setOtherStores] = useState([]);
  const [browseSearch, setBrowseSearch] = useState("");
  const [browseLoading, setBrowseLoading] = useState(false);
  const [browseGroups, setBrowseGroups] = useState([]); // one row per (product, store), stock summed across that store's lots

  useEffect(() => {
    if (!myStoreId) {
      setOtherStores([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const url = `${getAPI("storeNetwork")}?store=${myStoreId}&_ts=${Date.now()}`;
        const response = await Get(url);
        const networkStores = Array.isArray(response?.stores) ? response.stores : [];
        const others = networkStores.filter(
          (store) => store?.id && String(store.id) !== String(myStoreId),
        );
        if (!cancelled) setOtherStores(others);
      } catch (error) {
        console.error("Failed to load this store's business network:", error);
        // Fall back to the user's own membership list rather than showing
        // nothing, in case the new endpoint is unreachable (e.g. mid-deploy).
        if (!cancelled) {
          const others = (stores || []).filter(
            (store) => store?.id && String(store.id) !== String(myStoreId),
          );
          setOtherStores(others);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [Get, getAPI, stores, myStoreId]);

  const fetchStoreCatalog = useCallback(
    async (store, search) => {
      // The gateway lets a request act on another store the caller has
      // active membership on when a `store` query param is passed - this
      // is the same trust boundary "Switch to another store" uses, just
      // without actually switching the caller's active store.
      //
      // productInventoryData uses DRF's default PageNumberPagination
      // (fixed page size, no page_size override), so - same bug/fix as
      // the Deals page and Storefront Studio's product picker earlier -
      // a single page request would silently miss anything past the
      // first page. Loop through every page instead (capped as a
      // backstop against a runaway loop).
      const rows = [];
      let page = 1;
      let safety = 0;
      while (safety < 50) {
        const response = await GetPaginatedData("productInventoryData", {
          page,
          search,
          // `_ts` is a pure cache-buster: browsers can serve an identical GET
          // URL (same store + search text) from HTTP cache, which was hiding
          // stock-level changes (deductions/receipts) that happened between two
          // searches in the same tab. A changing query param forces a fresh hit.
          queryParams: { store: store.id, exclude_hidden_for_transfer: 1, _ts: Date.now() },
        });
        const pageRows = Array.isArray(response?.data) ? response.data : [];
        rows.push(...pageRows);
        if (!response?.nextPage || pageRows.length === 0) break;
        page += 1;
        safety += 1;
      }

      const byProduct = new Map();
      rows.forEach((row) => {
        const productId = row?.product;
        if (!productId) return;
        const key = productId;
        const existing = byProduct.get(key);
        const currentStock = Number(row?.current_stock || 0);
        const uomTitle = row?.segment_inventory?.[0]?.segment_uom || "";
        const sellingPrice = row?.selling_price ?? row?.segment_inventory?.[0]?.selling_price_per_pc ?? null;
        const purchasePrice = row?.purchase_price ?? row?.segment_inventory?.[0]?.purchase_price_per_pc ?? null;
        if (existing) {
          existing.availableStock += currentStock;
        } else {
          byProduct.set(key, {
            productId,
            storeId: store.id,
            storeName: store.name,
            title: row?.product_title || "Untitled product",
            barcode: row?.product_barcode || "",
            categories: (row?.product_category || []).map((c) => c.name).join(", "),
            uomTitle,
            sellingPrice,
            purchasePrice,
            availableStock: currentStock,
          });
        }
      });
      return Array.from(byProduct.values()).filter((g) => g.availableStock > 0);
    },
    [GetPaginatedData],
  );

  const searchAllStores = useCallback(
    async (search) => {
      const targets = otherStores;
      if (!targets.length) {
        setBrowseGroups([]);
        return;
      }
      setBrowseLoading(true);
      try {
        const perStoreResults = await Promise.all(
          targets.map((store) =>
            fetchStoreCatalog(store, search).catch((error) => {
              console.error(`Failed to search ${store.name}'s catalog:`, error);
              return [];
            }),
          ),
        );
        setBrowseGroups(perStoreResults.flat());
      } finally {
        setBrowseLoading(false);
      }
    },
    [otherStores, fetchStoreCatalog],
  );

  // ---- Transfer requests (My Requests) ----
  const [requestDirection, setRequestDirection] = useState("incoming"); // "incoming" = others asking ME | "outgoing" = requests I sent
  const [requests, setRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(false);

  const fetchRequests = useCallback(
    async (direction) => {
      setRequestsLoading(true);
      try {
        // Same default-pagination caveat as the catalog search above - loop
        // pages instead of trusting a single page_size request.
        const rows = [];
        let page = 1;
        let safety = 0;
        while (safety < 50) {
          const response = await GetPaginatedData("stockTransferRequest", {
            page,
            queryParams: { direction, _ts: Date.now() },
          });
          const pageRows = Array.isArray(response?.data) ? response.data : [];
          rows.push(...pageRows);
          if (!response?.nextPage || pageRows.length === 0) break;
          page += 1;
          safety += 1;
        }
        setRequests(rows);
      } catch (error) {
        console.error("Failed to load transfer requests:", error);
        toast.error("Could not load transfer requests.");
        setRequests([]);
      } finally {
        setRequestsLoading(false);
      }
    },
    [GetPaginatedData],
  );

  const createTransferRequest = async ({
    sourceStore,
    sourceStoreName,
    sourceProductId,
    productTitle,
    productBarcode,
    uomTitle,
    requestedQty,
    sourceSellingPrice,
    sourcePurchasePrice,
  }) => {
    const payload = {
      source_store: sourceStore,
      source_store_name: sourceStoreName,
      source_product: sourceProductId,
      product_title: productTitle,
      product_barcode: productBarcode || null,
      uom_title: uomTitle || null,
      requested_qty: requestedQty,
      source_selling_price: sourceSellingPrice ?? null,
      source_purchase_price: sourcePurchasePrice ?? null,
      requested_by_name: user?.first_name
        ? `${user.first_name} ${user.last_name || ""}`.trim()
        : user?.username || "",
    };
    const response = await Post("stockTransferRequest", payload);
    return response?.data;
  };

  const updateTransferStatus = async (
    transferId,
    { status, remarks, actorName, destinationProduct, sellingPrice, purchasePrice },
  ) => {
    const payload = { status };
    if (remarks) payload.remarks = remarks;
    if (actorName) payload.actor_name = actorName;
    // Only relevant for "received" without a linked existing product - lets
    // the requester confirm/adjust the selling price before the backend
    // auto-creates a matching product in their own catalog.
    if (sellingPrice !== undefined && sellingPrice !== null && sellingPrice !== "") {
      payload.selling_price = sellingPrice;
    }
    if (purchasePrice !== undefined && purchasePrice !== null && purchasePrice !== "") {
      payload.purchase_price = purchasePrice;
    }

    // Linking the requester's own EXISTING product happens as a normal
    // field patch (not part of the status pipeline) right before
    // "received" is sent, so the receive step stocks it there instead of
    // auto-creating a new one.
    if (destinationProduct) {
      await Patch("stockTransferRequest", transferId, { destination_product: destinationProduct });
    }

    const url = `${getAPI("stockTransferRequest")}${transferId}/update-status/`;
    const response = await Post(url, payload);
    return response?.data;
  };

  return {
    myStoreId,
    otherStores,
    browseSearch,
    setBrowseSearch,
    browseLoading,
    browseGroups,
    searchAllStores,

    requestDirection,
    setRequestDirection,
    requests,
    requestsLoading,
    fetchRequests,

    createTransferRequest,
    updateTransferStatus,

    Get,
    getAPI,
  };
};

export default StockTransferController;
